import { Link } from 'react-router';
import { SEO } from '@/components/seo/seo';

export function PrivacyPage() {
  return (
    <div className="relative">
      <SEO
        title="Privacy Policy"
        description="How Fixtyer collects, uses, and protects your personal data when you prepare a pothole damage claim."
        canonical="/privacy"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-16 lg:py-24">
        {/* Top diagonal stripe bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="flex items-center gap-2 text-sm text-brand-600 mb-6">
            <Link to="/" className="hover:text-brand-700 transition-colors">Home</Link>
            <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-800">Privacy Policy</span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold text-brand-950 leading-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-surface-800">
            {/* Keep as a fixed date: rendering "today" breaks hydration on the
                prerendered page and misstates when the policy last changed. */}
            Last updated: 19 July 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="bg-brand-50 rounded-2xl p-8 mb-12 border border-brand-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-brand-900 mt-0 mb-2">Your Privacy Matters</h2>
                  <p className="text-surface-800 m-0">
                    Fixtyer is committed to protecting your personal information. This policy explains how we collect, 
                    use, and safeguard your data when you use our document preparation service for pothole damage claims.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <PolicySection number={1} title="Who We Are">
              <p>
                Fixtyer is operated by <strong>Elevyn Technology Group Limited</strong>, a company registered in England and Wales.
                We provide a document preparation service that helps you prepare your own pothole damage claims using 
                templates, guidance, and AI-powered analysis.
              </p>
              <p>
                For any questions about this privacy policy or how we handle your data, please contact us at:{' '}
                <a href="mailto:privacy@fixtyer.co.uk" className="text-brand-600 hover:text-brand-700">privacy@fixtyer.co.uk</a>
              </p>
            </PolicySection>

            {/* Section 2 */}
            <PolicySection number={2} title="Information We Collect">
              <h4 className="font-display font-semibold text-brand-900">Account Information</h4>
              <ul>
                <li>Name and email address when you register</li>
                <li>Password (stored securely using industry-standard encryption)</li>
                <li>Contact details you choose to provide</li>
              </ul>

              <h4 className="font-display font-semibold text-brand-900">Claim Information</h4>
              <ul>
                <li>Details of the pothole incident (date, location, description)</li>
                <li>Photos and evidence you upload</li>
                <li>Vehicle and damage information</li>
                <li>Repair quotes and invoices</li>
              </ul>

              <h4 className="font-display font-semibold text-brand-900">Guest Check Information</h4>
              <p>
                In the public guest check, the road postcode is used transiently to identify the likely highway authority. The incident date, damage category and postcode remain in your browser&apos;s sessionStorage for the registration handoff and are not saved by Fixtyer unless you create and save a claim. Guest form values are not sent to Google Analytics.
              </p>

              <h4 className="font-display font-semibold text-brand-900">Technical Information</h4>
              <ul>
                <li>Device and browser information</li>
                <li>IP address and approximate location</li>
                <li>Usage patterns and interaction with our service</li>
              </ul>
            </PolicySection>

            {/* Section 3 */}
            <PolicySection number={3} title="How We Use Your Information">
              <p>We use your information to:</p>
              <ul>
                <li><strong>Provide our service</strong> — creating your claim pack, analysing evidence, and generating documents</li>
                <li><strong>Improve our AI analysis</strong> — helping our systems better understand evidence quality and claim requirements</li>
                <li><strong>Communicate with you</strong> — sending account and document-preparation updates, responding to enquiries, and providing support</li>
                <li><strong>Process payments</strong> — securely handling transactions through our payment provider</li>
                <li><strong>Measure site use and campaigns</strong> — using optional analytics and advertising storage only after you consent; funnel events use an anonymous, session-only journey identifier and exclude claim contents and contact details</li>
                <li><strong>Comply with legal obligations</strong> — maintaining records as required by law</li>
              </ul>
            </PolicySection>

            {/* Section 4 */}
            <PolicySection number={4} title="Legal Basis for Processing">
              <p>Under UK GDPR, we process your data based on:</p>
              <div className="grid sm:grid-cols-2 gap-4 my-6">
                <LegalBasisCard
                  title="Contract"
                  description="Processing necessary to provide you with our document preparation service"
                />
                <LegalBasisCard
                  title="Legitimate Interests"
                  description="Improving our services and preventing fraud"
                />
                <LegalBasisCard
                  title="Legal Obligation"
                  description="Compliance with tax, accounting, and other regulatory requirements"
                />
                <LegalBasisCard
                  title="Consent"
                  description="Optional analytics and advertising storage, and marketing communications where you opt in"
                />
              </div>
            </PolicySection>

            {/* Section 5 */}
            <PolicySection number={5} title="Data Sharing">
              <p>We do not sell your personal data. We may share information with:</p>
              <ul>
                <li><strong>Service providers</strong> — hosting, payment processing, and email services that help us operate</li>
                <li><strong>AI providers</strong> — for evidence analysis (data is processed securely and not used to train external models)</li>
                <li><strong>Legal authorities</strong> — if required by law or to protect our rights</li>
              </ul>
              <p>
                <strong>Important:</strong> We never share your claim data with councils or other authorities on your behalf. 
                You retain full control and submit claims yourself using the documents we help you prepare.
              </p>
            </PolicySection>

            {/* Section 6 */}
            <PolicySection number={6} title="Data Security">
              <p>We implement robust security measures including:</p>
              <ul>
                <li>Encryption of data in transit (TLS) and at rest</li>
                <li>Secure, access-controlled cloud infrastructure</li>
                <li>Regular security assessments and updates</li>
                <li>Limited employee access on a need-to-know basis</li>
              </ul>
            </PolicySection>

            {/* Section 7 */}
            <PolicySection number={7} title="Data Retention">
              <p>
                We retain account, claim and evidence data while your account is active and
                for as long as reasonably necessary to provide and support the service, resolve
                disputes, protect service security, and meet legal, accounting or regulatory
                obligations.
              </p>
              <p>
                You can request account or claim deletion through our published contact route.
                We will remove data we no longer need, subject to records we are legally required
                to retain. Residual encrypted backup copies expire through normal backup roll-off
                rather than being removed instantly.
              </p>
            </PolicySection>

            {/* Section 8 */}
            <PolicySection number={8} title="Your Rights">
              <p>Under UK data protection law, you have the right to:</p>
              <div className="space-y-3 my-6">
                <RightItem title="Access" description="Request a copy of the personal data we hold about you" />
                <RightItem title="Rectification" description="Ask us to correct inaccurate or incomplete data" />
                <RightItem title="Erasure" description="Request deletion of your data ('right to be forgotten')" />
                <RightItem title="Restriction" description="Ask us to limit how we use your data" />
                <RightItem title="Portability" description="Receive your data in a machine-readable format" />
                <RightItem title="Object" description="Object to processing based on legitimate interests" />
              </div>
              <p>
                To exercise any of these rights, contact us at{' '}
                <a href="mailto:hello@fixtyer.co.uk" className="text-brand-600 hover:text-brand-700">hello@fixtyer.co.uk</a>. 
                We'll respond within 30 days.
              </p>
            </PolicySection>

            {/* Section 9 */}
            <PolicySection number={9} title="Cookies">
              <p>
                We use essential cookies to make our service work. These include authentication cookies and 
                security tokens. Optional Google analytics and advertising storage stay denied unless you choose
                Accept analytics and advertising. Choosing Essential only denies all four optional Google consent
                categories: analytics storage, advertising storage, advertising user data and advertising
                personalisation.
              </p>
              <p>
                For detailed information about our cookie practices, please see our{' '}
                <Link to="/cookies" className="text-brand-600 hover:text-brand-700">Cookie Policy</Link>.
              </p>
            </PolicySection>

            {/* Section 10 */}
            <PolicySection number={10} title="Changes to This Policy">
              <p>
                We may update this privacy policy from time to time. We'll notify you of significant changes 
                via email or through our service. The "last updated" date at the top shows when this policy 
                was last revised.
              </p>
            </PolicySection>

            {/* Section 11 */}
            <PolicySection number={11} title="Contact & Complaints">
              <p>For privacy enquiries or to exercise your rights:</p>
              <div className="bg-surface-50 rounded-xl p-6 my-6 border border-surface-200">
                <p className="font-semibold text-brand-900 mb-2">Elevyn Technology Group Limited</p>
                <p className="text-surface-800 mb-1">Email: <a href="mailto:hello@fixtyer.co.uk" className="text-brand-600 hover:text-brand-700">hello@fixtyer.co.uk</a></p>
              </div>
              <p>
                If you're not satisfied with how we handle your data, you have the right to complain to the 
                Information Commissioner's Office (ICO):{' '}
                <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-700">
                  ico.org.uk
                </a>
              </p>
            </PolicySection>

          </div>
        </div>
      </section>

      {/* Back to Home CTA */}
      <section className="py-12 bg-brand-50 border-t border-brand-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-surface-800 mb-4">Have questions about how we protect your data?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
            >
              Contact Us
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-brand-700 font-semibold rounded-xl border-2 border-brand-200 hover:border-brand-300 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Component for policy sections
function PolicySection({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-8 bg-accent-500 text-white text-sm font-bold rounded-lg flex items-center justify-center">
          {number}
        </span>
        <h3 className="font-display text-2xl font-semibold text-brand-950 m-0">{title}</h3>
      </div>
      <div className="text-surface-800 [&>p]:mb-4 [&>ul]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul>li]:mb-2 [&>h4]:mt-6 [&>h4]:mb-3">
        {children}
      </div>
    </div>
  );
}

// Card for legal basis
function LegalBasisCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-surface-200">
      <h5 className="font-display font-semibold text-brand-900 mb-1">{title}</h5>
      <p className="text-sm text-surface-800 m-0">{description}</p>
    </div>
  );
}

// Component for rights items
function RightItem({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex items-start gap-3">
      <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
      <div>
        <span className="font-semibold text-brand-900">{title}:</span>{' '}
        <span className="text-surface-800">{description}</span>
      </div>
    </div>
  );
}
